import React, { useState } from 'react'
import { MdPushPin } from 'react-icons/md'

import { findMatchRange } from './utils'

/**
 * O título com o trecho buscado destacado. O recorte vem de `findMatchRange`, que devolve
 * posições do texto original — por isso o destaque sai acentuado mesmo quando o usuário digitou
 * sem acento.
 */
export const HighlightedTitle: React.FC<{
  title: string
  searchTerm?: string
}> = ({ title, searchTerm }) => {
  const range = searchTerm ? findMatchRange(title, searchTerm) : null

  if (!range) {
    return <>{title}</>
  }

  return (
    <>
      {title.slice(0, range.start)}
      <mark>{title.slice(range.start, range.end)}</mark>
      {title.slice(range.end)}
    </>
  )
}

interface IProps {
  route: string
  title: string
  isActive?: boolean
  isPinned: boolean
  searchTerm?: string
  onNavigate: (route: string) => void
  onTogglePin: (route: string) => void
  className?: string
  icon?: React.ReactNode
  /** Posição na lista de fixados; presente só quando o item pode ser reordenado. */
  orderIndex?: number
  onReorder?: (from: number, to: number) => void
  /** Quantos fixados existem — limite para o atalho de teclado. */
  orderCount?: number
}

/**
 * Item navegável do menu. O botão de fixar fica sobre o item e não navega — daí o
 * `stopPropagation` e o `preventDefault`, senão o clique no alfinete trocaria de tela.
 *
 * Nos fixados o item também é arrastável: `onReorder` recebe a posição de origem e a de destino.
 * Arrastar não alcança quem navega por teclado, então Alt+setas faz o mesmo movimento.
 */
export const MenuItem: React.FC<IProps> = ({
  route,
  title,
  isActive = false,
  isPinned,
  searchTerm,
  onNavigate,
  onTogglePin,
  className = '',
  icon,
  orderIndex,
  onReorder,
  orderCount = 0
}) => {
  const pinLabel = isPinned ? 'Remover dos fixados' : 'Fixar no topo do menu'

  const reorderable = typeof orderIndex === 'number' && Boolean(onReorder)

  const [isDragging, setIsDragging] = useState(false)
  const [isDropTarget, setIsDropTarget] = useState(false)

  function handleReorderKey(event: React.KeyboardEvent) {
    if (!reorderable || !event.altKey) {
      return
    }

    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const to = orderIndex + (event.key === 'ArrowDown' ? 1 : -1)

    if (to < 0 || to >= orderCount) {
      return
    }

    onReorder?.(orderIndex, to)
  }

  // O `title` do link cobre o nome longo, que trunca com reticências para não passar por baixo
  // do alfinete.
  return (
    <div
      className={`menu_item ${isActive ? 'is-active' : ''} ${className} ${
        isDragging ? 'is-dragging' : ''
      } ${isDropTarget ? 'is-drop-target' : ''}`}
      draggable={reorderable}
      onDragStart={
        reorderable
          ? e => {
              e.dataTransfer.effectAllowed = 'move'
              e.dataTransfer.setData('text/plain', String(orderIndex))
              setIsDragging(true)
            }
          : undefined
      }
      onDragEnd={reorderable ? () => setIsDragging(false) : undefined}
      onDragOver={
        reorderable
          ? e => {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              setIsDropTarget(true)
            }
          : undefined
      }
      onDragLeave={reorderable ? () => setIsDropTarget(false) : undefined}
      onDrop={
        reorderable
          ? e => {
              e.preventDefault()
              setIsDropTarget(false)

              const from = Number(e.dataTransfer.getData('text/plain'))

              if (Number.isNaN(from)) {
                return
              }

              onReorder?.(from, orderIndex)
            }
          : undefined
      }
    >
      <a
        href={route}
        data-menu-item
        title={
          reorderable
            ? `${title} — arraste para reordenar (ou Alt + setas)`
            : title
        }
        aria-current={isActive ? 'page' : undefined}
        onKeyDown={handleReorderKey}
        onClick={e => {
          e.preventDefault()
          onNavigate(route)
        }}
      >
        {icon}
        <span className="menu_item__title">
          <HighlightedTitle title={title} searchTerm={searchTerm} />
        </span>
      </a>
      <button
        type="button"
        className={`menu_item__pin ${isPinned ? 'is-pinned' : ''}`}
        title={pinLabel}
        aria-label={pinLabel}
        aria-pressed={isPinned}
        onClick={e => {
          e.preventDefault()
          e.stopPropagation()
          onTogglePin(route)
        }}
      >
        <MdPushPin />
      </button>
    </div>
  )
}
